/** Optional buffering of unsolicited document changes. Transport cursors stay outside this queue. */
export interface ChangeDeliveryOptions {
  delayMs?: number
  sliceMs?: number
}

export interface IncomingDocumentChange {
  type: string
  document?: { id: string }
}

export type ChangeDeliveryCompletion = void | Promise<void> | Map<string, Promise<void>>

export class ChangeDelivery<T extends IncomingDocumentChange> {
  private pending: T[] = []
  private timer: ReturnType<typeof setTimeout> | null = null
  private recentWrites = new Map<string, number>()
  private commandsRunning = 0
  private inFlight = new Map<string, Promise<void>>()

  constructor(
    private options: () => ChangeDeliveryOptions | null,
    private deliver: (changes: T[], sliceMs?: number) => ChangeDeliveryCompletion,
    private onError: (error: unknown) => void = error => console.error('Error delivering server changes', error),
  ) {}

  noteWrites(ids: string[]) {
    const expiresAt = Date.now() + 30_000
    for (const id of ids) this.recentWrites.set(id, expiresAt)
  }

  beginCommand() {
    this.commandsRunning += 1
  }

  endCommand() {
    this.commandsRunning = Math.max(0, this.commandsRunning - 1)
  }

  enqueue(change: T, urgent = false) {
    const options = this.options()
    const now = Date.now()
    for (const [id, expiresAt] of this.recentWrites) {
      if (expiresAt <= now) this.recentWrites.delete(id)
    }

    const ownWrite = this.recentWrites.has(change.document?.id)
    if (!options) {
      this.flush()
      this.dispatch([change])
      return
    }

    if (urgent || ownWrite || this.commandsRunning > 0) {
      // Do not let an earlier queued version overwrite an immediate update.
      const preceding = this.pending.filter(item => item.document?.id === change.document?.id)
      this.pending = this.pending.filter(item => item.document?.id !== change.document?.id)
      this.dispatch([...preceding, change], options.sliceMs ?? 4)
      return
    }

    this.pending.push(change)
    if (this.timer === null) {
      const delay = options.delayMs ?? 5000
      const boundedDelay = Number.isFinite(delay) ? Math.max(0, Math.min(5000, delay)) : 5000
      this.timer = setTimeout(() => this.flush(), boundedDelay)
    }
  }

  flush() {
    if (this.timer !== null) clearTimeout(this.timer)
    this.timer = null
    const changes = this.pending
    this.pending = []
    if (changes.length > 0) this.dispatch(changes, this.options()?.sliceMs ?? 4)
  }

  private dispatch(changes: T[], sliceMs?: number) {
    // Serialize versions of the same document without making an unrelated
    // immediate write wait for another document's asynchronous rights lookup.
    const groups = new Map<Promise<void> | undefined, T[]>()
    for (const change of changes) {
      const previous = this.inFlight.get(change.document?.id)
      const group = groups.get(previous) ?? []
      group.push(change)
      groups.set(previous, group)
    }
    for (const [previous, group] of groups) {
      const ids = [...new Set(group.map(change => change.document?.id).filter(Boolean))]
      const deliver = () => {
        try {
          return this.deliver(group, sliceMs)
        } catch (error) {
          this.onError(error)
        }
      }
      const delivery = previous ? previous.then(deliver) : deliver()
      for (const id of ids) {
        const completion = delivery instanceof Map
          ? delivery.get(id)
          : Promise.resolve(delivery).then(result => result instanceof Map ? result.get(id) : result)
        const settled = Promise.resolve(completion).catch(error => this.onError(error))
        this.inFlight.set(id, settled)
        void settled.then(() => {
          if (this.inFlight.get(id) === settled) this.inFlight.delete(id)
        })
      }
    }
  }

  clear() {
    if (this.timer !== null) clearTimeout(this.timer)
    this.timer = null
    this.pending = []
    this.recentWrites.clear()
    this.commandsRunning = 0
  }
}
